import { useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useRef, useLayoutEffect } from "react";
import BookingPageHead from "@/components/booking/bookingPageHead";
import { BookingStepperContainer } from "@/components/booking/bookingStepperContainer";
import BookingSuccessInfo from "@/components/booking/bookingSuccessInfo";
import BookingSummary from "@/components/booking/bookingSummary";
import BookingClientDetails from "@/components/booking/steps/bookingClientDetails";
import BookingCodeInput from "@/components/booking/steps/bookingCodeInput";
import BookingCollectCreditCard from "@/components/booking/steps/bookingCollectCreditCard";
import BookingDateTimeSelectionWithProvider from "@/components/booking/steps/bookingDateTimeSelectionWithProvider";
import BookingFrontDeskBooking from "@/components/booking/steps/bookingFrontDeskBooking";
import BookingPhoneConflict from "@/components/booking/steps/bookingPhoneConflict";
import BookingPhoneNumber from "@/components/booking/steps/bookingPhoneNumber";
import BookingReturningClient from "@/components/booking/steps/bookingReturningClient";
import BookingReturningClientEditCard from "@/components/booking/steps/bookingReturningClientEditCard";
import BookingReturningClientEditDetails from "@/components/booking/steps/bookingReturningClientEditDetails";
import BookingServicesSelection from "@/components/booking/steps/bookingServicesSelection";
import MedspaInfoCard from "@/components/common/medspaInfoCard";
import { MedspaLogo } from "@/components/common/medspaLogo";
import PageContainerWrapper from "@/components/common/pageContainer/pageContainerWrapper";
import PoweredByMoxieWithAffirm from "@/components/common/powerByMoxie/powerByMoxieWithAffirm";
import { FullWidthTopBar } from "@/components/common/topBar/fullWidthTopBar";
import PaymentPlansPromotionBanner from "@/components/serviceFlow/client/paymentPlansPromotionBanner";
import { useClientBookingContext } from "@/contexts/clientBookingContext";
import { MedspaInfoQueryResult } from "@/graphql/queries/medspaInfo.graphql.types";
import useSelectEligibleProviderEffect from "@/hooks/booking/useBookingProviderSelection";
import useMoxieMediaQuery from "@/hooks/misc/useMoxieMediaQuery";
import useUpdateBookingUrl from "@/hooks/settings/useBookingUrl";
import { BookingStep } from "@/types/bookingTypes";

export type MedspaInfo = MedspaInfoQueryResult["data"]["medspa"][number];

function BookingView() {
  const {
    step,
    setStep,
    medspaData,
    customizationEnabled, // This includes FF and Settings
    customizationPrimaryColor,
    customizationTextColor,
    hasMixedAppointmentTypes,
    cherryIntegrationV1Enabled,
    cherryPromotionEnabled,
    affirmPromotionEnabled,
    trackPhoneNumberClick,
  } = useClientBookingContext();

  const isBigScreen = useMoxieMediaQuery("ipad-hor");

  const isVerySmallScreen = useMediaQuery("(max-width:350px)");

  // Not using Moxie media queries since grid has responsive linked to original breakpoints
  const theme = useTheme();
  const isMDBreakpoint = useMediaQuery(theme.breakpoints.up("md"));

  useSelectEligibleProviderEffect();
  useUpdateBookingUrl();

  const disableSummary = [
    BookingStep.RETURNING_CLIENT_EDIT_CARD,
    BookingStep.RETURNING_CLIENT_EDIT_DETAILS,
    BookingStep.PHONE_CONFLICT,
  ].includes(step);

  const medspaLogoUrl = medspaData.additionalPublicInfo?.logo;

  const scrollWrapperRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (scrollWrapperRef.current && step === BookingStep.BOOKED) {
      scrollWrapperRef.current.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [step]);

  return (
    <>
      <BookingPageHead />
      <PageContainerWrapper
        topBarComponent={
          <FullWidthTopBar
            name={medspaData?.name}
            logoUrl={medspaLogoUrl}
            barTitle="Book your appointment"
            customizationEnabled={customizationEnabled}
          />
        }
        additionalBannerComponent={
          cherryIntegrationV1Enabled && (
            <PaymentPlansPromotionBanner
              cherryPromotionEnabled={cherryPromotionEnabled}
              affirmPromotionEnabled={affirmPromotionEnabled}
            />
          )
        }
        sx={{
          height: "fit-content",
          minHeight: "100vh",
          pt: "1rem",
          px: { md: "120px" },
          mb: hasMixedAppointmentTypes ? "180px" : "120px",
          ...(isVerySmallScreen ? { px: 1 } : {}),
        }}
        scrollWrapperRef={scrollWrapperRef}
      >
        <Grid
          container
          spacing={{ mobile: 0, "ipad-ver": 4 }}
          sx={{
            mt: 2,
            height: "100%",
            justifyContent: "center",
          }}
        >
          <Grid size={{ xs: 12, md: step === BookingStep.BOOKED ? 6 : 4 }}>
            {step === BookingStep.BOOKED && <BookingSuccessInfo />}
            <MedspaInfoCard
              name={medspaData?.name}
              phone={medspaData?.contactPhone}
              email={medspaData?.contactEmail}
              address={medspaData?.address}
              medspaLogo={
                customizationEnabled && medspaLogoUrl ? (
                  <MedspaLogo
                    medspaName={medspaData.name}
                    medspaLogoUrl={medspaLogoUrl}
                  />
                ) : undefined
              }
              customPrimaryColor={
                customizationEnabled ? customizationPrimaryColor : undefined
              }
              handlePhoneNumberClick={trackPhoneNumberClick}
            />
            {isBigScreen && !disableSummary && <BookingSummary />}
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <BookingStepperContainer
              step={step}
              customizationEnabled={customizationEnabled}
              customizationPrimaryColor={customizationPrimaryColor}
              customizationTextColor={customizationTextColor}
              poweredByMoxie={
                isMDBreakpoint ? (
                  <PoweredByMoxieWithAffirm
                    medspaSlug={medspaData.slug}
                    affirmEnabled={
                      cherryIntegrationV1Enabled && affirmPromotionEnabled
                    }
                  />
                ) : null
              }
            >
              {
                {
                  [BookingStep.SERVICES]: <BookingServicesSelection />,
                  [BookingStep.AVAILABILITY_AND_PROVIDER_SELECTION]: (
                    <BookingDateTimeSelectionWithProvider />
                  ),
                  [BookingStep.PHONE_NUMBER]: <BookingPhoneNumber />,
                  [BookingStep.CODE_INPUT]: <BookingCodeInput />,
                  [BookingStep.RETURNING_CLIENT]: <BookingReturningClient />,
                  [BookingStep.RETURNING_CLIENT_EDIT_DETAILS]: (
                    <BookingReturningClientEditDetails />
                  ),
                  [BookingStep.RETURNING_CLIENT_EDIT_CARD]: (
                    <BookingReturningClientEditCard />
                  ),
                  [BookingStep.CLIENT_DETAILS]: <BookingClientDetails />,
                  [BookingStep.PHONE_CONFLICT]: (
                    <BookingPhoneConflict
                      medspaName={medspaData.name}
                      medspaPhone={medspaData.contactPhone}
                      goBackOnClick={() => setStep(BookingStep.PHONE_NUMBER)}
                    />
                  ),
                  [BookingStep.FRONT_DESK_BOOKING]: <BookingFrontDeskBooking />,
                  [BookingStep.COLLECT_CREDIT_CARD_INFO]: (
                    <BookingCollectCreditCard />
                  ),
                }[step]
              }
            </BookingStepperContainer>
          </Grid>
          {!isBigScreen && !disableSummary && (
            <Grid size={12}>
              <BookingSummary />
            </Grid>
          )}
        </Grid>
      </PageContainerWrapper>
    </>
  );
}

export default BookingView;
